import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/Buttons/Button";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { toast } from "react-toastify";
import { getUserRolePermissions, getUsers } from '../../../services/userServicer';
import Select from 'react-select';
import { getControls } from "src/services/controls";
import { useForm } from "react-hook-form";
import { MdOutlineDelete } from "react-icons/md";
import { createSurvey, fetchDataApi, fetchQuestionByReference } from "src/services/question"
import { useNavigate } from 'react-router-dom';
import i18n from "src/i18n";

import { getReferences } from "src/services/reference";
import LoadingScreen from "../../../components/Loading/LoadingScreen";

interface QuestionState {
  en_name: string;
  ar_name: string;
  en_description: string;
  ar_description: string;
  questions: string[];
  users: string[];
  reference_id: string[];
}

const CreateSurvey = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notAllowed, setNotAllowed] = useState(false);
  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch, setValue, trigger } = useForm();
  const [question, setQuestion] = useState<QuestionState>({
    en_name: '',
    ar_name: '',
    en_description: '',
    ar_description: '',
    questions: [],
    users: [],
    reference_id: []
  });

  const [references, setReferences] = useState<any[]>([{ reference: null, points: [{ label: "", value: "" }], selectedpoints: [{ point: null, controls: [] }] }]);
  const [referenceOptions, setReferenceOptions] = useState<any[]>([]);
  const [usersOptions, setUsersOptions] = useState<any[]>([]);
  const [questionsOptions, setQuestionsOptions] = useState<{ value: string; label: string }[]>([]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setQuestion((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReferenceChange = async (index: number, selectedOption: any) => {
    const newReferences = [...references];
    newReferences[index].reference = selectedOption;
    setReferences(newReferences); // Update references immediately for UI
  
    try {
      const response = await fetchQuestionByReference(selectedOption.value);
      if (response.success) {
        const relatedQuestions = response.data.map((question: any) => ({
          value: question.id,
          label: i18n.language === "ar" ? question.question_ar : question.question_en,
        }));
  
        setQuestionsOptions(relatedQuestions);
  
        setQuestion((prev) => ({
          ...prev,
          questions: relatedQuestions.map((question: any) => question.value),
        }));
  
        setValue("questions", relatedQuestions);
      } else {
        toast.error("Error fetching related questions");
      }
    } catch (error) {
      toast.error("Error fetching related questions");
    }
  };

  const onSubmit = async (data: any) => {
   
      if(question.en_name !== ''){
        question.ar_name = question.en_name
      }
      if(question.en_description !== ''){
        question.ar_description = question.en_description
      }
    const response = await createSurvey(question);
    if (response.success) {
      toast.success(response.message);
      navigate('/surveys');
    } else {
      toast.error(response.message);
    }
  };

  const fetchUsers = async () => {
    try {
      const response: any = await getUsers("", 10000, 0);
      if (response && response.success) {
        const indexedData = response.data.map((user: any, idx: number) => ({
          value: user.id,
          label: user.name
        }));
        setUsersOptions(indexedData);
      } else if (response && response.data.length === 0) {
        setUsersOptions([]);
      } else {
        toast.error("Failed to fetch users data");
      }
    } catch (error: any) {
      toast.error("Error fetching users data:", error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const questions = await fetchDataApi(`bank-questions?perPage=${500}&pageNo=${1}&query=${''}`);
      if (questions.success) {
        let questionsOptions = questions.data.map((question: any) => ({
          value: question.id,
          label: i18n.language === 'ar' ? question.question_ar : question.question_en
        }));
        setQuestionsOptions(questionsOptions);
      } else {
        toast.error(questions.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const fetchUserRolePermissions = async () => {
      try {
        setLoading(true);
       // let userId: any = localStorage.getItem('userId');
        const permissions = await getUserRolePermissions();
       
        if (permissions.success) {
          const compliancePagePermission = permissions.data.permissions.find((permission: any) => permission.page === "survey");
          if (compliancePagePermission) {
           
            setUserPermissions(compliancePagePermission);
            if (compliancePagePermission.permissions.create) {
              await fetchUsers();
              await fetchQuestions();
              setNotAllowed(false);
            } else {
              setNotAllowed(true);
            }
          } else {
            toast.error(permissions.message);
          }
        }
      } catch (error: any) {
        toast.error(t("permissions.Error_fetching_user_role_permissions") + ":", error);
      } finally {
        setLoading(false);
      }
    };
    const fetchReferences = async () => {
      try {
        const data = await getReferences('', 500, 0);
        if (data && data.success) {
          const options = data?.data?.map((ref: any) => ({ value: ref.id, label: ref.name }));
          setReferenceOptions(options);
        } else {
          toast.error(data[0].message, { autoClose: 5000, className: 'toast-error' });
        }
      } catch (error: any) {
        toast.error(error, { autoClose: 5000, className: 'toast-error' });
        console.error('Error fetching references:', error);
      }
    };

    fetchUserRolePermissions();
    fetchReferences()
  }, []);

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '50px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: '40px',
      padding: '0 6px',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '40px',
    }),
  };

  if (loading) {
    return (
      <div className="loading-screen" 
      style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                     <div><LoadingScreen/></div>
                   </div>
    );
  }

  if (!loading) {

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
        <BreadcrumbItem>{t("surveys.Surveys")}</BreadcrumbItem>
        <BreadcrumbItem active style={{ paddingRight: '10px' }}>
          {t("surveys.Create Survey")}
        </BreadcrumbItem>
      </Breadcrumb>

      <h5 className="mb-lg">{t('surveys.Create Survey')}</h5>
      {
        !notAllowed && <div className="container_info">
          <form onSubmit={handleSubmit(onSubmit)} className="create-compliance-form">
            <div className="form-section2 mb-5">
              <h5 className="section-title">{t('surveys.BasicInfo')}</h5>
              <div className="form-group">
                <label>{t('surveys.NameEn')}</label>
                <input
                  type="text"
                  value={question.en_name}
                  placeholder={t('surveys.NameEnPlaceholder') || ''}
                  className="form-control"
                  {...register("en_name", { required: true })}
                  onChange={handleInputChange}
                />
                {errors.en_name && <span className="text-danger">{t('This field is required')}</span>}
              </div>
              {/*<div className="form-group">
                <label>{t('surveys.NameAr')}</label>
                <input
                  type="text"
                  value={question.ar_name}
                  placeholder={t('surveys.NameArPlaceholder') || ''}
                  className="form-control"
                  {...register("ar_name", { required: true })}
                  onChange={handleInputChange}
                />
                {errors.ar_name && <span className="text-danger">{t('This field is required')}</span>}
              </div>*/}
              <div className="form-group">
                <label>{t('surveys.en_description')}</label>
                <input
                  type="text"
                  value={question.en_description}
                  placeholder={t('surveys.en_description_placeholder') || ''}
                  className="form-control"
                  {...register("en_description", { required: true })}
                  onChange={handleInputChange}
                />
                {errors.en_description && <span className="text-danger">{t('This field is required')}</span>}
              </div>
              {/*<div className="form-group">
                <label>{t('surveys.ar_description')}</label>
                <input
                  type="text"
                  value={question.ar_description}
                  placeholder={t('surveys.ar_description_placeholder') || ''}
                  className="form-control"
                  {...register("ar_description", { required: true })}
                  onChange={handleInputChange}
                />
                {errors.ar_description && <span className="text-danger">{t('This field is required')}</span>}
              </div>*/}
            </div>

            <div className="form-section2 mb-5">
              <h5 className="section-title">{t('surveys.questions')}</h5>

              <div className="form-group">
                {references.map((reference, refIndex) => (
                  <div className="form-group" key={refIndex} style={{ flex: '1 1 80%' }}>
                    <label>{t('Reference')}</label>
                    <Select
                      name={`reference-${refIndex}`}
                      options={referenceOptions}
                      className="basic-single"
                      classNamePrefix="select"
                      onChange={(selectedOption) => handleReferenceChange(refIndex, selectedOption)}
                      styles={customSelectStyles}
                    />
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label className="form-label">{t("questions")}</label>
                <Select
                  options={questionsOptions}
                  isMulti={true}
                  value={questionsOptions.filter((option) => question.questions.includes(option.value))} // Pre-select questions
                  isDisabled={true} // Disable user interaction
                  className="basic-single"
                  classNamePrefix="select"
                  styles={customSelectStyles}
                  onChange={(selectedOptions) => {
                    setQuestion((prev) => ({
                      ...prev,
                      questions: selectedOptions ? selectedOptions?.map((option) => option.value) : [],
                    }));
                    setValue("questions", selectedOptions ? selectedOptions?.map((option) => option.value) : []);
                  }}
                />
              </div>
              <button type="submit" className="submit-button4"  style={{marginTop: '20px',marginBottom: '20px',width: '100%'}}>
                {t("Create Survey")}
              </button>
            </div>
{/* 
            <div className="form-section2 mb-5">
              <h5 className="section-title">{t('surveys.users')}</h5>
              <div className="form-group">
                <label className="form-label">{t("surveys.users")}</label>
                <Select
                  options={usersOptions}
                  isMulti={true}
                  className="basic-single"
                  classNamePrefix="select"
                  styles={customSelectStyles}
                  {...register("users")}
                  onChange={(selectedOptions: any) => {
                    setQuestion((prev) => ({
                      ...prev,
                      users: selectedOptions ? selectedOptions?.map((option: any) => option.value) : []
                    }));
                    setValue("users", selectedOptions ? selectedOptions?.map((option: any) => option.value) : []);
                    trigger("users");
                  }}
                />
              </div>


             
            </div> */}

           
          </form>
        </div>
      }

      {notAllowed && <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
        </div>
      </div>}
    </div>
  );

}
};

export default CreateSurvey;